import { ApiClient } from "@/services/apiClient";
import type { MappedFilterValues } from "@/composables/filter";
import type { CategoryId } from "@/services/collection/categories";
import type { CollectibleId } from "@/services/collectibles";
import type { Sort } from "@/composables/marketplace";
import type { CollectionItemElastic } from "./collectionItemElasticSchema";
import { CollectionItemElastic_Schema, CollectionItemElasticSearchResponse_Schema, type CollectionItemElasticSearchResponse } from "./collectionItemElasticSchema";
import type { CollectionItemId } from "@/services/collection/item/collectionItemTypes";

export type SearchRequest = {
  page: number;
  perPage: number;
  sellable: boolean;
  query?: string;
  categoryId?: CategoryId;
  collectibleId?: CollectibleId;
  ownerId?: number;
  filters?: MappedFilterValues;
  sort?: Sort;
};

type SearchOptions = {
  signal?: AbortSignal;
};

export const searchCollectionItemsElastic = async (request: SearchRequest, options?: SearchOptions): Promise<CollectionItemElasticSearchResponse> => {
  const baseUrl = "api-public/collection/collection-item-elastic";

  const { page, perPage, query, categoryId, ownerId, sellable, filters, sort, collectibleId } = request;

  // Build base params
  const params: Record<string, string | number | boolean | undefined> = {
    page,
    perPage,
    query: query || undefined,
    categoryId: categoryId ?? undefined,
    collectibleId: collectibleId ?? undefined,
    ownerId: ownerId ?? undefined,
    sellable,
    sort: sort || undefined,
  };

  // Build filter query string with URLSearchParams (supports duplicate keys)
  const filterParams = new URLSearchParams();
  if (filters) {
    for (const [key, valueIds] of Object.entries(filters)) {
      // Range filters: key like "-5_min" -> parameters[][-5][min]=value
      const rangeMatch = key.match(/^(.+)_(min|max)$/);
      if (rangeMatch) {
        const [, definitionId, bound] = rangeMatch;
        filterParams.append(`parameters[][${definitionId}][${bound}]`, valueIds[0]);
      } else {
        for (const valueId of valueIds) {
          filterParams.append(`parameters[][${key}]`, valueId);
        }
      }
    }
  }

  // Append filter params to URL if any
  const filterQueryString = filterParams.toString();
  const url = filterQueryString ? `${baseUrl}?${filterQueryString}` : baseUrl;

  const response = await ApiClient.getPublic(url, params, undefined, undefined, options?.signal);
  return CollectionItemElasticSearchResponse_Schema.parse(response.data);
};

export const fetchCollectionItemElasticById = async (id: CollectionItemId): Promise<CollectionItemElastic> => {
  const url = `api-public/collection/collection-item-elastic/${id}`;
  const response = await ApiClient.getPublic<CollectionItemElastic>(url);
  return CollectionItemElastic_Schema.parse(response.data);
};
